import { duracaoAgendamentoRecord } from '@agenda/model-agenda'
import { css } from '@emotion/react'
import { Cell, Grid, useTheme } from 'bold-ui'
import { dateFnsDefaultLocale } from 'components/agenda/agendaLocalizer'
import { SelectField } from 'components/form'
import { CustomSingleSelectFieldProps, ErrorField } from 'components/form/core'
import { InfoIcon } from 'components/InfoIcon'
import { addMinutes, format } from 'date-fns'
import { useHorarioAgendaSelectFieldQuery } from 'graphql/hooks.generated'
import { ReactComponent as AgendaOnlineIcon } from 'images/agenda/agenda-online.svg'
import { useCallback, useEffect, useMemo } from 'react'
import { useField } from 'react-final-form'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'

import { getHorarioInicialStatus as getHorarioInicialStatusPure } from './getHorarioInicialStatus'
import { getHorariosNoDia } from './getHorariosNoDia'
import { getItemsHorarioFinal } from './getItemsHorarioFinal'
import { getProximoHorarioIndisponivel } from './getProximoHorarioIndisponivel'
import { hasHorarioOcupadoNoPeriodo } from './hasHorarioOcupadoNoPeriodo'

export interface HorarioAgendaSelectFieldModel {
  inicial: Date
  final: Date
}

export type HorarioAgendaSelectFieldProps = DistributiveOmit<
  CustomSingleSelectFieldProps<Date>,
  'name' | 'label' | 'value'
> & {
  name: MetaPath<HorarioAgendaSelectFieldModel>
  lotacaoId: ID
  outraLotacaoIdVerificarDisponibilidade?: ID
  showHorarioFinal?: boolean
  dia: Date | undefined
  value?: HorarioAgendaSelectFieldModel
  labels?: { inicial: string; final?: string }
  verificarReservasAgendaOnline?: boolean
  atencaoDomiciliar?: boolean
  limitarDuracaoMaxima?: boolean
  agendamentosIdsDesconsiderar?: Array<ID>
}

const itemToString = (item: Date) => item && format(item, 'HH:mm', { locale: dateFnsDefaultLocale })

export function HorarioAgendaSelectField(props: Readonly<HorarioAgendaSelectFieldProps>) {
  const {
    name,
    lotacaoId,
    outraLotacaoIdVerificarDisponibilidade,
    value,
    dia,
    showHorarioFinal = false,
    labels = { inicial: 'Horário inicial', final: 'Horário final' },
    disabled,
    verificarReservasAgendaOnline = true,
    atencaoDomiciliar = false,
    limitarDuracaoMaxima = true,
    agendamentosIdsDesconsiderar,
    ...rest
  } = props

  const theme = useTheme()

  const isDiaVazio = !dia

  const { data: { horariosAgenda } = {}, loading } = useHorarioAgendaSelectFieldQuery({
    variables: {
      input: {
        lotacaoId,
        outraLotacaoIdVerificarDisponibilidade,
        dia: dateAsYyyyMmDd(dia),
        isAtencaoDomiciliar: atencaoDomiciliar,
        agendamentosIdsDesconsiderar: agendamentosIdsDesconsiderar?.filterNotNull(),
      },
    },
    skip: isDiaVazio || disabled || !lotacaoId,
  })

  const {
    input: { value: horarioInicial, onChange: onChangeHorarioInicial, onBlur: onBlurHorarioInicial },
  } = useField<Date>(name.inicial.absolutePath())
  const {
    input: { value: horarioFinal, onChange: onChangeHorarioFinal },
  } = useField<Date>(name.final.absolutePath())

  // TODO https://github.com/laboratoriobridge/pec/issues/29585
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHorarioInicial = useCallback(onChangeHorarioInicial, [name.inicial])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHorarioFinal = useCallback(onChangeHorarioFinal, [name.final])

  const horariosMap = useMemo(() => new Map(horariosAgenda?.map((h) => [new Date(h.horario), h])), [horariosAgenda])

  const horariosIniciais = useMemo(() => Array.from(horariosMap.keys()).sort((a, b) => Number(a) - Number(b)), [
    horariosMap,
  ])

  const handleChangeHorarioInicial = useCallback(
    (novoHorarioInicial: Date) => {
      const slot = novoHorarioInicial && horariosMap.get(novoHorarioInicial)

      setHorarioFinal(slot ? addMinutes(novoHorarioInicial, slot.duracao) : undefined)
    },
    [horariosMap, setHorarioFinal]
  )

  useEffect(() => {
    if (loading) return

    if (!horarioInicial) {
      setHorarioFinal(undefined)
      return
    }

    if (horariosMap.has(horarioInicial)) return

    const { inicial, final } = getHorariosNoDia({ inicial: horarioInicial, final: horarioFinal }, horariosMap)

    setHorarioInicial(inicial)
    setHorarioFinal(final)

    if (!isDiaVazio) {
      onBlurHorarioInicial() // Necessário para revalidar o campo
    }
  }, [
    horarioInicial,
    horarioFinal,
    horariosMap,
    loading,
    setHorarioInicial,
    setHorarioFinal,
    onBlurHorarioInicial,
    isDiaVazio,
  ])

  const getHorarioInicialStatus = (h: Date | undefined) =>
    getHorarioInicialStatusPure(h, horariosMap, verificarReservasAgendaOnline)

  const horariosAgendaNormalizados = useMemo(
    () =>
      horariosAgenda?.map((h) => ({
        ...h,
        isOcupado: h.isOcupado || (verificarReservasAgendaOnline && h.isReservadoAgendaOnline),
      })),
    [horariosAgenda, verificarReservasAgendaOnline]
  )

  const proximoHorarioOcupado = useMemo(
    () => getProximoHorarioIndisponivel(horarioInicial, horariosAgendaNormalizados),
    [horarioInicial, horariosAgendaNormalizados]
  )

  const duracaoMaximaHorarioFinal = getDuracaoMaximaHorarioFinal(atencaoDomiciliar, limitarDuracaoMaxima)

  const horariosFinais = useMemo(
    () =>
      showHorarioFinal && horarioInicial
        ? getItemsHorarioFinal(horariosMap, horarioInicial, proximoHorarioOcupado, duracaoMaximaHorarioFinal)
        : [],
    [duracaoMaximaHorarioFinal, horarioInicial, horariosMap, proximoHorarioOcupado, showHorarioFinal]
  )

  const hintHorarioFinal =
    !!duracaoMaximaHorarioFinal &&
    `O tamanho máximo de um agendamento é de ${duracaoMaximaHorarioFinal} minutos. Por isso o campo Horário final só exibe horários que estejam dentro do tamanho máximo do agendamento.`

  return (
    <Grid alignItems='flex-start'>
      <Cell size={showHorarioFinal ? 6 : 12}>
        <SelectField<Date>
          label={labels.inicial}
          value={value?.inicial}
          items={horariosIniciais}
          itemToString={itemToString}
          itemIsEqual={(h) => getHorarioInicialStatus(h) !== 'disponivel'}
          customText={(h) => (getHorarioInicialStatus(h) === 'ocupado' ? 'Ocupado' : 'Reservado')}
          selectedItemStyle={(h) =>
            getHorarioInicialStatus(h) === 'ocupado'
              ? { icon: 'banOutline', color: theme.pallete.text.disabled }
              : { icon: AgendaOnlineIcon, color: theme.pallete.status.info.main }
          }
          selectedItemTooltip={(h) =>
            getHorarioInicialStatus(h) === 'reservadoAgendaOnline' ? 'Reservado para agenda online' : ''
          }
          name={name.inicial}
          disabled={disabled}
          validate={(h) => (getHorarioInicialStatus(h) !== 'disponivel' ? 'Horário ocupado' : undefined)}
          {...rest}
          onChange={handleChangeHorarioInicial}
        />
        <ErrorField name={name} ignoreObjectError />
      </Cell>
      {showHorarioFinal && (
        <Cell size={6}>
          <SelectField<Date>
            label={
              hintHorarioFinal ? (
                <span css={styles.labelHorarioFinal}>
                  {labels.final}
                  <InfoIcon icon='infoCircleFilled' text={hintHorarioFinal} />
                </span>
              ) : (
                labels.final
              )
            }
            value={value?.final}
            items={horariosFinais}
            itemToString={itemToString}
            name={name.final}
            disabled={disabled || !horarioInicial}
            validate={(horarioFinal) =>
              hasHorarioOcupadoNoPeriodo(horarioInicial, horarioFinal, horariosAgenda, verificarReservasAgendaOnline)
                ? 'Horário ocupado'
                : undefined
            }
            {...rest}
          />
        </Cell>
      )}
    </Grid>
  )
}

const getDuracaoMaximaHorarioFinal = (
  atencaoDomiciliar: boolean,
  limitarDuracaoMaxima: boolean
): number | undefined => {
  if (!limitarDuracaoMaxima) return undefined

  const tipoLimiteDuracao = atencaoDomiciliar ? 'atencaoDomiciliar' : 'padrao'
  return duracaoAgendamentoRecord[tipoLimiteDuracao].maximoEmMinutos
}

const styles = {
  labelHorarioFinal: css`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  `,
}
